import { Box, Paper, Skeleton, Stack } from '@mui/material';
import { useId, type ReactNode } from 'react';
import { designTokens } from '../tokens';
import { visuallyHiddenStyles } from './visuallyHidden';

export type ContentSkeletonVariant = 'table' | 'list' | 'profile' | 'cards';

export type ContentSkeletonProps = {
  variant?: ContentSkeletonVariant;
  /** 화면에는 표시하지 않는 접근 가능한 로딩 안내입니다. */
  label?: ReactNode;
  rows?: number;
  columns?: number;
  rowHeight?: number;
  embedded?: boolean;
};

function SkeletonLine({ width = '100%', height = designTokens.loading.skeleton.lineHeight }: { width?: number | string; height?: number }) {
  return <Skeleton variant="rounded" width={width} height={height} sx={{ maxWidth: '100%', borderRadius: height <= designTokens.loading.skeleton.lineHeight ? designTokens.radius.pill : designTokens.loading.skeleton.shapeRadius }} />;
}

/** 데이터 대신 화면의 형태만 보여 줍니다. 이전 데이터의 보관과 노출 여부는 feature가 결정합니다. */
export function ContentSkeleton({
  variant = 'list',
  label = '데이터를 불러오는 중입니다.',
  rows = 5,
  columns = 4,
  rowHeight,
  embedded = false,
}: ContentSkeletonProps) {
  const labelId = useId();
  const rowCount = Number.isFinite(rows) ? Math.max(1, Math.min(12, Math.floor(rows))) : 5;
  const columnCount = Number.isFinite(columns) ? Math.max(1, Math.min(8, Math.floor(columns))) : 4;
  const baseRowHeight = variant === 'table' ? designTokens.layout.resourceRowHeight : designTokens.layout.listSkeletonRowHeight;
  const resolvedRowHeight = typeof rowHeight === 'number' && Number.isFinite(rowHeight) ? Math.max(baseRowHeight, rowHeight) : baseRowHeight;
  const lineRows = Array.from({ length: rowCount });

  return <Paper
    data-skeleton={variant}
    role="status"
    aria-labelledby={labelId}
    aria-live="polite"
    aria-busy="true"
    square={embedded}
    elevation={0}
    variant={embedded ? undefined : 'outlined'}
    sx={{
      height: '100%',
      minHeight: 132,
      minWidth: 0,
      overflow: 'hidden',
      bgcolor: embedded ? 'transparent' : undefined,
      border: embedded ? 0 : undefined,
      borderRadius: embedded ? 0 : designTokens.radius.surface,
      p: `${designTokens.layout.sectionCardBodyPadding}px`,
      '@media (prefers-reduced-motion: reduce)': {
        '& .MuiSkeleton-root, & .MuiSkeleton-root::after': { animation: 'none' },
      },
    }}
  >
    <Box id={labelId} sx={visuallyHiddenStyles}>{label}</Box>
    <Box aria-hidden="true">
      {variant === 'table' ? <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: 2, pb: 1.5, borderBottom: `1px solid ${designTokens.color.tableCellDivider}` }}>
          {Array.from({ length: columnCount }, (_, index) => <SkeletonLine key={index} width="55%" />)}
        </Box>
        {lineRows.map((_, rowIndex) => <Box key={rowIndex} sx={{ display: 'grid', gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: 2, alignItems: 'center', minHeight: resolvedRowHeight, borderBottom: `1px solid ${designTokens.color.tableCellDivider}` }}>
          {Array.from({ length: columnCount }, (_, columnIndex) => <SkeletonLine key={columnIndex} width={`${60 + ((rowIndex + columnIndex) % 3) * 15}%`} />)}
        </Box>)}
      </Box> : variant === 'profile' ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 3 }}>
        <Stack spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
          <Skeleton variant="circular" width={80} height={80} />
          <SkeletonLine width={104} height={designTokens.loading.skeleton.headingHeight} />
          <SkeletonLine width={136} />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          {Array.from({ length: 6 }, (_, index) => <Stack key={index} spacing={1}><SkeletonLine width="35%" height={12} /><SkeletonLine width="75%" /></Stack>)}
        </Box>
      </Box> : variant === 'cards' ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: `${designTokens.layout.pageSectionGap}px` }}>
        {Array.from({ length: Math.min(rowCount, 4) }, (_, index) => <Stack key={index} spacing={2} sx={{ p: 2, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface }}>
          <SkeletonLine width="45%" height={20} />
          <SkeletonLine width="90%" />
          <SkeletonLine width="65%" />
          <SkeletonLine width={88} height={designTokens.layout.sectionCardActionHeight} />
        </Stack>)}
      </Box> : <Stack spacing={2}>
        {lineRows.map((_, index) => <Stack key={index} direction="row" spacing={1.5} sx={{ minHeight: resolvedRowHeight, alignItems: 'center' }}>
          <Skeleton variant="rounded" width={designTokens.layout.listSkeletonRowHeight} height={designTokens.layout.listSkeletonRowHeight} sx={{ flexShrink: 0, borderRadius: designTokens.radius.portrait }} />
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}><SkeletonLine width={`${50 + (index % 3) * 10}%`} /><SkeletonLine width="80%" height={12} /></Stack>
          <SkeletonLine width={48} height={designTokens.layout.valueBadgeHeight} />
        </Stack>)}
      </Stack>}
    </Box>
  </Paper>;
}
